import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { BookingStatusSchema } from '../../meetings/booking/booking';
import { VerificationCaseKindSchema } from '../../verification/verification-case-kind/verification-case-kind';
import { VerificationCaseStatusSchema } from '../../verification/verification-case-status/verification-case-status';

/** The member's latest verification case — enough to name it and open it, never the case itself. */
export const MemberVerificationActivitySchema = z
  .object({
    caseId: UuidSchema,
    kind: VerificationCaseKindSchema,
    status: VerificationCaseStatusSchema,
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type MemberVerificationActivity = z.infer<typeof MemberVerificationActivitySchema>;

/**
 * Violations against the member, as counts. `standing` is what still weighs on
 * the account (enforced, not reverted); `total` is every row ever recorded,
 * detections nobody acted on included.
 */
export const MemberViolationActivitySchema = z
  .object({
    standing: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
  })
  .strict();

export type MemberViolationActivity = z.infer<typeof MemberViolationActivitySchema>;

export const MemberNextBookingSchema = z
  .object({
    bookingId: UuidSchema,
    status: BookingStatusSchema,
    startsAt: z.iso.datetime(),
  })
  .strict();

export type MemberNextBooking = z.infer<typeof MemberNextBookingSchema>;

/** Meetings the member is a party to. `next` is the soonest live one still ahead, or `null`. */
export const MemberBookingActivitySchema = z
  .object({
    total: z.number().int().nonnegative(),
    upcoming: z.number().int().nonnegative(),
    next: MemberNextBookingSchema.nullable(),
  })
  .strict();

export type MemberBookingActivity = z.infer<typeof MemberBookingActivitySchema>;

/**
 * What the members page links out to (P27c2): the pointers into the modules
 * that hold a member's history, composed by the users module over the query
 * bus. Counts and ids only — the content stays behind each module's own door
 * and its own permission.
 */
export const MemberActivitySchema = z
  .object({
    verification: MemberVerificationActivitySchema.nullable(),
    violations: MemberViolationActivitySchema,
    bookings: MemberBookingActivitySchema,
  })
  .strict();

export type MemberActivity = z.infer<typeof MemberActivitySchema>;
